// Geocoding service using Nominatim (OpenStreetMap)
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

/**
 * Convert an address or ZIP code to latitude/longitude coordinates
 * @param {string} address - Address or ZIP code to geocode
 * @returns {Promise<Object|null>} Location object with lat, lng, and name, or null if not found
 */
export const geocodeAddress = async (address) => {
  try {
    // Detect if input is just a ZIP code (5 digits or 5+4 format)
    const zipCodePattern = /^\d{5}(-\d{4})?$/;
    const isZipCode = zipCodePattern.test(address.trim());

    const inputZip = address.trim().substring(0, 5);

    // Build query parameters — use structured postalcode search for zip codes
    // to avoid Nominatim fuzzy-matching invalid zips to random locations
    const params = new URLSearchParams({
      format: 'json',
      limit: '5',
      addressdetails: '1',
      countrycodes: 'us'
    });

    if (isZipCode) {
      params.set('postalcode', inputZip);
      params.set('country', 'us');
    } else {
      params.set('q', address);
    }

    const url = `${NOMINATIM_URL}?${params}`;

    console.log('Geocoding:', isZipCode ? `postalcode=${inputZip}` : address, 'with country filter: us');

    const response = await fetch(url, {
      headers: {
        'User-Agent': 'KnowBeforeYouGo/1.0' // Nominatim requires a User-Agent
      }
    });

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.length > 0) {
      // Filter results to only US locations
      const usResults = data.filter(result =>
        result.address &&
        (result.address.country_code === 'us' ||
         result.address.country === 'United States' ||
         result.display_name.includes('United States'))
      );

      // For zip code searches, cross-validate that the returned postcode matches.
      // For address searches, only accept results that resolve to a meaningful place
      // (city, town, village, county, state, or postcode) — not random street/house matches.
      const ACCEPTED_TYPES = [
        'city', 'town', 'village', 'hamlet', 'suburb', 'county',
        'state', 'postcode', 'administrative'
      ];

      const validResults = isZipCode
        ? usResults.filter(result => result.address?.postcode === inputZip)
        : usResults.filter(result => {
            const type = result.type || '';
            const category = result.class || '';
            // Accept place/boundary results at city/town/county level
            if (category === 'place' || category === 'boundary') {
              return ACCEPTED_TYPES.includes(type);
            }
            // Also accept results that have a resolved city/town in the address
            const addr = result.address || {};
            return !!(addr.city || addr.town || addr.village);
          });

      if (validResults.length > 0) {
        const result = validResults[0];

        // Extract city and state from address details
        const addr = result.address || {};
        const city = addr.city || addr.town || addr.village || addr.county || '';
        const state = addr.state || '';
        const cityState = city && state ? `${city} - ${state}` : result.display_name;

        return {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          name: result.display_name,
          cityState: cityState
        };
      }
    }

    return null;

  } catch (error) {
    console.error('Error geocoding address:', error);
    return null;
  }
};

/**
 * Reverse geocode coordinates to an address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string|null>} Address string or null if not found
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`;

    const response = await fetch(url, {
      headers: {
        'User-Agent': 'KnowBeforeYouGo/1.0'
      }
    });

    if (!response.ok) {
      throw new Error(`Reverse geocoding API error: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.display_name) {
      return data.display_name;
    }

    return null;

  } catch (error) {
    console.error('Error reverse geocoding:', error);
    return null;
  }
};
